import React from "react";
import VideoBlogLayout from "../../components/VideoBlogLayout";

const BlogTryAIReceptionistSandbox = () => (
  <VideoBlogLayout
    category="Video Walkthrough"
    eyebrow="Sandbox Tour"
    title="How to try out the AI receptionist in your sandbox account"
    subtitle="A short tour of where to find the AI receptionist in your sandbox, what to expect when you call it, and which settings are safe to play with before you're fully provisioned."
    author="Asha Team"
    date="April 24, 2026"
    readTime="3 min watch"
    videoUrl="https://www.loom.com/embed/816a67f524534a69a766f8f60d018718"
    videoTitle="Trying the AI Receptionist in Your Asha Sandbox"
    videoProvider="loom"
  >
    <div className="space-y-14">
      <div>
        <p className="text-lg text-gray-600 leading-relaxed mb-4">
          One of the fastest ways to get a feel for the Asha AI Operating
          System is to actually call the AI receptionist in your sandbox and
          hear it talk back. This walkthrough shows you where to find it, how
          to call it, and what's safe to tweak before your account is fully
          configured.
        </p>
        <p className="text-lg text-gray-600 leading-relaxed">
          A quick note up front:{" "}
          <strong className="text-gray-900 font-semibold">
            don't call the number in the video.
          </strong>{" "}
          That one is tied to a different sandbox. Yours has its own dedicated
          number in the same spot.
        </p>
      </div>

      <div>
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 tracking-tight">
          Where the AI receptionist lives in the sandbox
        </h2>
        <p className="text-lg text-gray-600 leading-relaxed mb-4">
          When you log in to your sandbox account, head to the{" "}
          <strong className="text-gray-900 font-semibold">AI Staff</strong>{" "}
          tab in the left navigation. You'll see a list of AI agents — open
          the AI receptionist.
        </p>
        <p className="text-lg text-gray-600 leading-relaxed">
          Inside that screen there's a{" "}
          <strong className="text-gray-900 font-semibold">
            sample phone number dedicated to your sandbox.
          </strong>{" "}
          Call it and you'll hit your sandbox's AI receptionist. The same
          screen also shows example call transcripts so you can see how calls
          look once they start coming in.
        </p>
      </div>

      <div className="rounded-2xl border border-amber-100 bg-amber-50/60 p-6">
        <h3 className="text-lg font-bold text-amber-900 mb-2">
          Sandbox ≠ production
        </h3>
        <p className="text-base text-gray-700 leading-relaxed">
          Sandbox accounts are{" "}
          <strong className="text-gray-900 font-semibold">
            not fully configured or provisioned.
          </strong>{" "}
          They give you a feel for the receptionist, but production
          environments run on higher-tier AI models and are noticeably more
          intelligent and faster. Treat the sandbox call as a preview, not the
          ceiling.
        </p>
      </div>

      <div>
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 tracking-tight">
          What you can customize from settings
        </h2>
        <p className="text-lg text-gray-600 leading-relaxed mb-4">
          The settings panel for the receptionist gives you lots of levers. A
          few of the most useful ones to play with in the sandbox:
        </p>
        <ul className="space-y-3 text-lg text-gray-600 leading-relaxed">
          <li className="flex gap-3">
            <span className="text-blue-600 mt-1.5 flex-shrink-0">•</span>
            <span>
              <strong className="text-gray-900 font-semibold">
                Intro message
              </strong>{" "}
              — change exactly how the AI greets callers.
            </span>
          </li>
          <li className="flex gap-3">
            <span className="text-blue-600 mt-1.5 flex-shrink-0">•</span>
            <span>
              <strong className="text-gray-900 font-semibold">Name</strong>{" "}
              — rename the receptionist to whatever fits your practice.
            </span>
          </li>
          <li className="flex gap-3">
            <span className="text-blue-600 mt-1.5 flex-shrink-0">•</span>
            <span>
              <strong className="text-gray-900 font-semibold">
                Practice-name pronunciation
              </strong>{" "}
              — teach it how to say tricky practice names correctly.
            </span>
          </li>
          <li className="flex gap-3">
            <span className="text-blue-600 mt-1.5 flex-shrink-0">•</span>
            <span>
              <strong className="text-gray-900 font-semibold">
                Voice (15+ preloaded)
              </strong>{" "}
              — swap between voices, listen to a few, and see which one
              matches the tone you want.
            </span>
          </li>
        </ul>
        <p className="text-lg text-gray-600 leading-relaxed mt-4">
          You'll also see controls for alerts and notifications, call
          handling, transfers, and agent custom context. Because the sandbox
          isn't fully provisioned, we don't recommend going deep on those
          yet — wait until your Account Manager has the production account
          set up.
        </p>
      </div>

      <div>
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 tracking-tight">
          Don't see a voice you love? Tell us.
        </h2>
        <p className="text-lg text-gray-600 leading-relaxed">
          The 15 preloaded voices are a starting point. If none of them feel
          right, ping your Account Manager. We can configure friendlier
          voices, faster voices, voices in different languages, and dial in
          the intelligence level so the receptionist matches exactly what
          your practice needs.
        </p>
      </div>

      <div className="rounded-2xl border border-blue-100 bg-blue-50/60 p-6">
        <h3 className="text-lg font-bold text-blue-900 mb-2">
          When you're ready, call your sandbox number
        </h3>
        <p className="text-base text-gray-700 leading-relaxed">
          Open the AI receptionist screen in your sandbox, grab the number
          shown there (
          <strong className="text-gray-900 font-semibold">
            not the one in this video
          </strong>
          ), and call it. The receptionist will walk you through the call
          flow. Remember — this is a taste, not the production experience.
          Once we configure the real thing, it gets a lot smarter and faster.
        </p>
      </div>
    </div>
  </VideoBlogLayout>
);

export default BlogTryAIReceptionistSandbox;
